import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { findAnalytics, analyticsDestination } from "../lib/analyticsSearch";
import "./AnalyticsHeaderSearch.css";

export default function AnalyticsHeaderSearch({ allowAI = false }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(0);
  const container = useRef(null);
  const input = useRef(null);
  const id = useId();
  const navigate = useNavigate();
  const location = useLocation();
  const results = useMemo(() => findAnalytics(query, allowAI), [query, allowAI]);
  useEffect(() => {
    const close = event => { if (!container.current?.contains(event.target)) setOpen(false); };
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, []);
  useEffect(() => { setOpen(false); }, [location.key]);
  useEffect(() => {
    if (open) document.getElementById(`${id}-option-${selected}`)?.scrollIntoView({ block: "nearest" });
  }, [open, selected, id]);
  const choose = item => {
    setOpen(false);
    input.current?.blur();
    navigate(analyticsDestination(item, location));
  };
  const keyDown = event => {
    if (event.key === "Escape") { setOpen(false); event.preventDefault(); }
    else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault(); setOpen(true);
      if (results.length) setSelected(current => !open ? 0 : (current + (event.key === "ArrowDown" ? 1 : -1) + results.length) % results.length);
    } else if (event.key === "Enter" && open && results[selected]) {
      event.preventDefault(); choose(results[selected]);
    }
  };
  return (
    <div className="analytics-header-search" ref={container}
      onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false); }}>
      <div className="analytics-search-field">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5" /><path d="m16 16 5 5" /></svg>
        <input ref={input} type="text" role="combobox" aria-label="Search analytics"
          aria-expanded={open} aria-controls={`${id}-list`} aria-autocomplete="list"
          aria-activedescendant={open && results[selected] ? `${id}-option-${selected}` : undefined}
          autoComplete="off" spellCheck="false" maxLength={160}
          placeholder="Search analytics, metrics, reports…" value={query}
          onFocus={() => { setSelected(0); setOpen(true); }} onKeyDown={keyDown}
          onChange={event => { setQuery(event.target.value); setSelected(0); setOpen(true); }} />
        {query && <button type="button" aria-label="Clear analytics search" className="analytics-search-clear"
          onClick={() => { setQuery(""); setSelected(0); input.current?.focus(); }}>×</button>}
      </div>
      {open && <div className="analytics-search-dropdown">
        <div className="analytics-search-caption" role="status" aria-live="polite">
          {query.trim() ? `${results.length} matching destinations` : "Browse analytics tabs • Try farm, RM, QA or sales"}
        </div>
        <ul id={`${id}-list`} role="listbox" aria-label="Analytics destinations" className="analytics-search-results">
          {results.map((item, index) => <li id={`${id}-option-${index}`} key={item.id} role="option"
            aria-selected={selected === index} className={selected === index ? "is-selected" : ""}
            onMouseDown={event => event.preventDefault()} onClick={() => choose(item)}>
            <div className="analytics-search-result-title">{item.title}</div>
            <div className="analytics-search-result-meta"><span>CEO Command Center › {item.tabLabel}</span><span>{item.partial ? "Related · " : ""}{item.kind}</span></div>
          </li>)}
        </ul>
        {!results.length && <div className="analytics-search-empty">No matching analytics. Try a shorter keyword, such as farm, RM or sales.</div>}
        <div className="analytics-search-footer">↑ ↓ to browse · Enter to open · Esc to close</div>
      </div>}
    </div>
  );
}
